import type { PrhCommonDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/prh';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { getPaymentMethod, LOB_CODE } from '../../constants/coverable';
import { isAllPRHCoverablesWithReportOrUWI } from '../../utils/coverablesUtil';
import type { OPCReceiptPageChangeType } from '../../recoil/types/opcTypes';

interface DecisionInfo {
    isWinbackOffer: boolean | undefined;
    paymentMethod: string;
    paymentMethodOnIssuance?: string;
    newInsurances: number;
    changedInsurances: number;
    coverableAmount: number;
    isExistingCustomer: boolean | undefined;
    coverables?: CoverableDTO[];
    getHippoText: (resourceKey: string) => string;
}

interface DecisionInfoCVR {
    paymentMethod: string;
    coverableAmount: number;
    email: string;
    getHippoText: (resourceKey: string) => string;
}

interface HeaderConfig {
    title: string;
    subTitle?: string;
    bulletPoints: string[];
    buttonText: string;
}

interface HeaderConfigCVR {
    title: string;
    subtitle: string;
    bulletPoints: string[];
}

const getDefaultConfig = ({
    paymentMethod,
    coverableAmount,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    let title: string = '';
    const bulletPoints: string[] = [];
    let buttonText: string = '';

    title = getHippoText('DSS-RECEIPT-PAGE-PURCHASE-HEADER');

    // Change the text to be plural depending on the amount of coverables
    if (coverableAmount === 1) {
        bulletPoints.push(getHippoText('DSS-RECEIPT-PAGE-SINGLE-COVERABLES-SUBHEADER'));
        buttonText = getHippoText('DSS-RECEIPT-PAGE-SINGLE-COVERABLE-BUTTON');
    } else {
        bulletPoints.push(getHippoText('DSS-RECEIPT-PAGE-MULT-COVERABLES-SUBHEADER'));
        buttonText = getHippoText('DSS-RECEIPT-PAGE-MULT-COVERABLE-BUTTON');
    }

    // Change text depending on payment method
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const paymentTextSubHeader = getHippoText(`DSS-RECEIPT-PAGE-${paymentMethodKey}-SUBHEADER`);
    bulletPoints.push(paymentTextSubHeader);

    return { title, bulletPoints, buttonText };
};

const getWinbackReceiptConfig = ({
    paymentMethod,
    coverableAmount,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    const singularOrPluralKey = coverableAmount === 1 ? 'SINGLE' : 'MULT';
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const subHeaders = getBulletList(
        getHippoText(
            `DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-${singularOrPluralKey}-COVERABLE-SUBHEADER`
        )
    );
    const paymentTextSubHeader = getHippoText(
        `DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-${paymentMethodKey}-SUBHEADER`
    );
    return {
        title: getHippoText('DSS-RECEIPT-PAGE-WINBACK-OFFER-HEADER'),
        subTitle: getHippoText(
            `DSS-RECEIPT-PAGE-WINBACK-OFFER-${singularOrPluralKey}-INSURANCE-SUBHEADER`
        ),
        buttonText: getHippoText(
            `DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-${singularOrPluralKey}-COVERABLE-BUTTON`
        ),
        bulletPoints: [...subHeaders, paymentTextSubHeader]
    };
};

const getBulletList = (textToSplit: string): string[] => {
    return textToSplit.split('-').filter((text) => text.length > 0);
};

const getNewCustomerBuysNewInsurancesConfig = ({
    paymentMethod,
    paymentMethodOnIssuance,
    coverableAmount,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    let title: string = '';
    let bulletPoints: string[] = [];
    let buttonText: string = '';
    if (paymentMethodOnIssuance === paymentMethod) {
        title = getHippoText('DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-HEADER');
    } else {
        title = getHippoText('DSS-RECEIPT-PAGE-NEW-CUSTOMER-THANK-YOU-HEADER');
    }

    // Change the text to be plural depending on the amount of coverables
    if (coverableAmount === 1) {
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-SINGLE-COVERABLE-BUTTON'
        );
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-SINGLE-COVERABLE-SUBHEADER'
        );
        bulletPoints = getBulletList(subHeader);
    } else {
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-MULT-COVERABLE-BUTTON'
        );
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-MULT-COVERABLES-SUBHEADER'
        );
        bulletPoints = getBulletList(subHeader);
    }

    // Change text depending on payment method
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const paymentTextSubHeader = getHippoText(
        `DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-${paymentMethodKey}-SUBHEADER`
    );
    bulletPoints.push(paymentTextSubHeader);

    return { title, bulletPoints, buttonText };
};

const getCVRBuysAgrInsuranceConfig = ({
    paymentMethod,
    coverableAmount,
    email,
    getHippoText
}: DecisionInfoCVR): HeaderConfigCVR => {
    const bulletPoints: string[] = [];
    const title = getHippoText('DSS-ALTERNATIVE-RECEIPT-PAGE-TITLE-CVR');
    const subtitle = getHippoText('DSS-ALTERNATIVE-RECEIPT-PAGE-SUBTITLE-CVR');

    if (coverableAmount === 1) {
        bulletPoints.push(getHippoText('DSS-ALTERNATIVE-RECEIPT-PAGE-TAKE-CARE-INSURANCE-CVR'));
        bulletPoints.push(getHippoText('DSS-ALTERNATIVE-RECEIPT-PAGE-RECEIVE-NEW-POLICY-CVR'));
    } else {
        bulletPoints.push(getHippoText('DSS-ALTERNATIVE-RECEIPT-PAGE-TAKE-CARE-INSURANCES-CVR'));
        bulletPoints.push(getHippoText('DSS-ALTERNATIVE-RECEIPT-PAGE-RECEIVE-NEW-POLICIES-CVR'));
    }

    // Change text depending on payment method
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const paymentTextSubHeader = getHippoText(
        `DSS-ALTERNATIVE-RECEIPT-PAGE-${paymentMethodKey}-CVR`
    );
    bulletPoints.push(paymentTextSubHeader);

    const emailText = `${getHippoText('DSS-ALTERNATIVE-RECEIPT-PAGE-EMAIL-CVR')} ${email}`;
    bulletPoints.push(emailText);

    return { title, subtitle, bulletPoints };
};

const getExistingCustomerBuysNewInsurancesConfig = ({
    paymentMethod,
    paymentMethodOnIssuance,
    coverableAmount,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    let title: string = '';
    let bulletPoints: string[] = [];
    let buttonText: string = '';

    if (paymentMethodOnIssuance === paymentMethod) {
        title = getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-HEADER');
    } else {
        title = getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-THANK-YOU-HEADER');
    }

    // Change the text to be plural depending on the amount of coverables
    if (coverableAmount === 1) {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-SINGLE-COVERABLE-SUBHEADER'
        );
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-SINGLE-COVERABLE-BUTTON'
        );
        bulletPoints = getBulletList(subHeader);
    } else {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-MULT-COVERABLES-SUBHEADER'
        );
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-MULT-COVERABLE-BUTTON'
        );
        bulletPoints = getBulletList(subHeader);
    }

    // Change text depending on payment method
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const paymentTextSubHeader = getHippoText(
        `DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-${paymentMethodKey}-SUBHEADER`
    );
    bulletPoints.push(paymentTextSubHeader);

    return { title, bulletPoints, buttonText };
};

const getAcceptChangeForMissingInformationUpdateConfig = ({
    getHippoText
}: DecisionInfo): HeaderConfig => {
    const title: string = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-UPDATE-MISSING-ATTRIBUTE-HEADER'
    );
    const subTitle: string = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-UPDATE-MISSING-ATTRIBUTE-SUBHEADER'
    );
    const bulletPoints = getBulletList(
        getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-UPDATE-MISSING-ATTRIBUTE-TEXT'
        )
    );
    const buttonText: string = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-BUTTON-TEXT'
    );
    return { title, subTitle, bulletPoints, buttonText };
};

const getAcceptChangeForAdjustingDeductibleOrSumConfig = ({
    paymentMethod,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    const title: string = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-DEDUCTIBLE-INSURANCE-SUM-HEADER'
    );
    const subTitle: string = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-DEDUCTIBLE-INSURANCE-SUM-SUBHEADER'
    );
    let bulletPoints: string[] = [];
    bulletPoints = getBulletList(
        getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-DEDUCTIBLE-INSURANCE-SUM-TEXT'
        )
    );
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const paymentText = getHippoText(
        `DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-${paymentMethodKey}-SUBHEADER`
    );
    bulletPoints.push(paymentText);
    const buttonText: string = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-BUTTON-TEXT'
    );
    return { title, subTitle, bulletPoints, buttonText };
};

const getAcceptChangeForAddCoverageConfig = ({
    paymentMethod,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    const title: string = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-ADD-COVERAGE-HEADER'
    );
    const subTitle: string = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-ADD-COVERAGE-SUBHEADER'
    );
    let bulletPoints: string[] = [];
    bulletPoints = getBulletList(
        getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-ADD-COVERAGE-TEXT')
    );
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const paymentText = getHippoText(
        `DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-ADD-COVERAGE-${paymentMethodKey}-SUBHEADER`
    );
    bulletPoints.push(paymentText);
    const buttonText: string = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-BUTTON-TEXT'
    );
    return { title, subTitle, bulletPoints, buttonText };
};

const getExistingCustomerAcceptsChangesConfig = ({
    paymentMethod,
    coverableAmount,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    let title: string = '';
    let bulletPoints: string[] = [];
    let buttonText: string = '';

    title = getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-HEADER');

    // Change the text to be plural depending on the amount of coverables
    if (coverableAmount === 1) {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-SINGLE-COVERABLE-SUBHEADER'
        );
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-SINGLE-COVERABLE-BUTTON'
        );
        bulletPoints = getBulletList(subHeader);
    } else {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-MULT-COVERABLES-SUBHEADER'
        );
        buttonText = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-MULT-COVERABLE-BUTTON'
        );
        bulletPoints = getBulletList(subHeader);
    }

    // Change text depending on payment method
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const paymentTextSubHeader = getHippoText(
        `DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-${paymentMethodKey}-SUBHEADER`
    );
    bulletPoints.push(paymentTextSubHeader);

    return { title, bulletPoints, buttonText };
};

const getExistingCustomerBuysInsurancesAndAcceptsChangesConfig = ({
    paymentMethod,
    newInsurances,
    changedInsurances,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    let title: string = '';
    let bulletPoints: string[] = [];
    let buttonText: string = '';

    title = getHippoText('DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-HEADER');
    buttonText = getHippoText(
        'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-MULT-COVERABLE-BUTTON'
    );

    // Change the text to be plural depending on the amount of added and changed coverables
    if (newInsurances === 1 && changedInsurances === 1) {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-SINGLE-COVERABLE-SUBHEADER'
        );
        bulletPoints = getBulletList(subHeader);
    } else {
        const subHeader = getHippoText(
            'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-MULT-COVERABLES-SUBHEADER'
        );
        bulletPoints = getBulletList(subHeader);
    }

    // Change text depending on payment method
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const paymentTextSubHeader = getHippoText(
        `DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-${paymentMethodKey}-SUBHEADER`
    );
    bulletPoints.push(paymentTextSubHeader);

    return { title, bulletPoints, buttonText };
};

const getOutputBuildingReportPendingInsuranceConfig = ({
    paymentMethod,
    coverables,
    isExistingCustomer,
    changedInsurances,
    newInsurances,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    const bulletPoints: string[] = [];
    const title = getHippoText(
        getDefaultTitle(isExistingCustomer, changedInsurances, newInsurances)
    );
    const subTitle = getHippoText('DSS-RECEIPT-PAGE-OUTPUT-PENDING-REPORT-HEADER');
    const buttonText = addHeaderBulletsAndSetButtonText(coverables, bulletPoints, getHippoText);

    addBuildingReportBulletPoints(coverables, bulletPoints, getHippoText);

    // Change text depending on payment method
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const paymentTextSubHeader = getHippoText(`DSS-RECEIPT-PAGE-${paymentMethodKey}-SUBHEADER`);
    bulletPoints.push(paymentTextSubHeader);

    return { title, bulletPoints, buttonText, subTitle };
};

const addHeaderBulletsAndSetButtonText = (
    coverables: CoverableDTO[] | undefined,
    bulletPoints: string[],
    getHippoText: (resourceKey: string) => string
): string => {
    if (isAllPRHCoverablesWithReportOrUWI(coverables ?? [])) {
        return '';
    }
    if (coverables && coverables.length === 1) {
        bulletPoints.push(getHippoText('DSS-RECEIPT-PAGE-VIEW-SINGLE-INSURANCE-SUBHEADER'));
        return getHippoText('DSS-RECEIPT-PAGE-SINGLE-COVERABLE-BUTTON');
    }
    bulletPoints.push(getHippoText('DSS-RECEIPT-PAGE-VIEW-MULT-INSURANCE-SUBHEADER'));
    return getHippoText('DSS-RECEIPT-PAGE-MULT-COVERABLE-BUTTON');
};

const addBuildingReportBulletPoints = (
    coverables: CoverableDTO[] | undefined,
    bulletPoints: string[],
    getHippoText: (resourceKey: string) => string
): void => {
    const buildingReportAmount =
        coverables?.filter(
            (coverable) =>
                coverable.lobCode === LOB_CODE.PRIVATE_HOUSE &&
                (coverable as PrhCommonDTO)?.outputBuildingReportPending
        )?.length ?? 0;
    if (buildingReportAmount === 1) {
        bulletPoints.push(
            getHippoText('DSS-RECEIPT-PAGE-WAIT-FOR-CONDITION-REPORT-SINGLE-INSURANCE-SUBHEADER')
        );
    } else {
        bulletPoints.push(
            getHippoText('DSS-RECEIPT-PAGE-WAIT-FOR-CONDITION-REPORT-MULT-INSURANCE-SUBHEADER')
        );
    }
};

const addInspectionBulletPoints = (
    coverables: CoverableDTO[] | undefined,
    bulletPoints: string[],
    getHippoText: (resourceKey: string) => string
): void => {
    const inspectionAmount =
        coverables?.filter(
            (coverable) => coverable.lobCode === 'PRH' && (coverable as PrhCommonDTO)?.hasUWI
        )?.length ?? 0;
    if (inspectionAmount === 1) {
        bulletPoints.push(
            getHippoText('DSS-RECEIPT-PAGE-WAIT-FOR-CONDITION-REPORT-SINGLE-INSURANCE-SUBHEADER')
        );
    } else {
        bulletPoints.push(
            getHippoText('DSS-RECEIPT-PAGE-WAIT-FOR-CONDITION-REPORT-MULT-INSURANCE-SUBHEADER')
        );
    }
};

const getDefaultTitle = (
    isExistingCustomer: boolean | undefined,
    changedInsurances: number,
    newInsurances: number
): string => {
    if (!isExistingCustomer && !changedInsurances)
        return 'DSS-RECEIPT-PAGE-NEW-CUSTOMER-NEW-PURCHASE-HEADER';
    if (isExistingCustomer && !changedInsurances)
        return 'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PURCHASE-HEADER';
    if (isExistingCustomer && changedInsurances && !newInsurances)
        return 'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-ACCEPT-CHANGES-HEADER';
    if (isExistingCustomer && changedInsurances && newInsurances)
        return 'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-PURCHASE-AND-CHANGES-HEADER';
    return 'DSS-RECEIPT-PAGE-PURCHASE-HEADER';
};

const getInspectionConfig = ({
    paymentMethod,
    coverables,
    isExistingCustomer,
    changedInsurances,
    newInsurances,
    getHippoText
}: DecisionInfo): HeaderConfig => {
    const bulletPoints: string[] = [];
    const title = getHippoText(
        getDefaultTitle(isExistingCustomer, changedInsurances, newInsurances)
    );
    const subTitle = getHippoText('DSS-RECEIPT-PAGE-HOUSE-INSPECTION-HEADER');

    const buttonText = addHeaderBulletsAndSetButtonText(coverables, bulletPoints, getHippoText);
    addInspectionBulletPoints(coverables, bulletPoints, getHippoText);

    // Change text depending on payment method
    const paymentMethodKey = getPaymentMethod(paymentMethod);
    const paymentTextSubHeader = getHippoText(`DSS-RECEIPT-PAGE-${paymentMethodKey}-SUBHEADER`);
    bulletPoints.push(paymentTextSubHeader);

    return { title, bulletPoints, buttonText, subTitle };
};

const getHeaderConfig = (
    isWinbackOffer: boolean | undefined,
    paymentMethod: string,
    paymentMethodOnIssuance: string | undefined,
    newInsurances: number,
    changedInsurances: number,
    isExistingCustomer: boolean | undefined,
    getHippoText: (resourceKey: string) => string,
    getOPCReceiptPageChangeType: OPCReceiptPageChangeType | undefined,
    coverables?: CoverableDTO[]
): HeaderConfig => {
    const decisionInfo: DecisionInfo = {
        isWinbackOffer,
        paymentMethod,
        paymentMethodOnIssuance,
        getHippoText,
        newInsurances,
        isExistingCustomer,
        changedInsurances,
        coverables,
        coverableAmount: newInsurances + changedInsurances
    };
    // Insurance has one or more coverables with building report pending
    if (isWinbackOffer) {
        return getWinbackReceiptConfig(decisionInfo);
    } else if (
        coverables?.some(
            (coverable) =>
                coverable.lobCode === 'PRH' &&
                (coverable as PrhCommonDTO)?.outputBuildingReportPending
        )
    ) {
        return getOutputBuildingReportPendingInsuranceConfig(decisionInfo);
    }
    // Insurance prompts an inspection due to UWI
    else if (
        coverables?.some(
            (coverable) => coverable.lobCode === 'PRH' && (coverable as PrhCommonDTO)?.hasUWI
        )
    ) {
        return getInspectionConfig(decisionInfo);
    }
    // New customer buys new insurance(s)
    else if (!isExistingCustomer && !changedInsurances) {
        return getNewCustomerBuysNewInsurancesConfig(decisionInfo);
    }
    // Existing customer buys new insurance(s)
    else if (isExistingCustomer && !changedInsurances) {
        return getExistingCustomerBuysNewInsurancesConfig(decisionInfo);
    }
    // Existing customer accepts changes for updating missing attributes (Chip ID on pet or odometer reading on car)
    else if (
        isExistingCustomer &&
        changedInsurances &&
        getOPCReceiptPageChangeType === 'updateMissingAttribute'
    ) {
        return getAcceptChangeForMissingInformationUpdateConfig(decisionInfo);
    }

    // Existing customer accepts changes for the deductible/insurance sum
    else if (
        isExistingCustomer &&
        changedInsurances &&
        (getOPCReceiptPageChangeType === 'adjustCoverage' ||
            getOPCReceiptPageChangeType === 'carOdometerReading')
    ) {
        return getAcceptChangeForAdjustingDeductibleOrSumConfig(decisionInfo);
    }

    // Existing customer accepts change for adding the coverage
    else if (
        isExistingCustomer &&
        changedInsurances &&
        getOPCReceiptPageChangeType === 'addCoverage'
    ) {
        return getAcceptChangeForAddCoverageConfig(decisionInfo);
    }

    // Existing customer accepts changes to one or more coverables
    else if (isExistingCustomer && changedInsurances && !newInsurances) {
        return getExistingCustomerAcceptsChangesConfig(decisionInfo);
    }
    // Existing customer buys insurance to one or more coverables and accepts changes to one or more coverables
    else if (isExistingCustomer && changedInsurances && newInsurances) {
        return getExistingCustomerBuysInsurancesAndAcceptsChangesConfig(decisionInfo);
    }

    return getDefaultConfig(decisionInfo);
};

const getCVRHeaderConfig = (
    paymentMethod: string,
    coverableAmount: number,
    email: string,
    getHippoText: (resourceKey: string) => string
): HeaderConfigCVR => {
    const decisionInfo: DecisionInfoCVR = {
        paymentMethod,
        coverableAmount,
        email,
        getHippoText
    };

    return getCVRBuysAgrInsuranceConfig(decisionInfo);
};

export { getHeaderConfig, getCVRHeaderConfig };
